import React, { useState, useEffect, useCallback, useRef } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import Portal from '../../shared/Portal';
import { s_TI } from '../../shared/styles';

// ── Dark form palette ─────────────────────────────────────────────────────────

const F = {
  bg:          '#14141a',
  panel:       '#1e1e28',
  border:      'rgba(255,255,255,0.1)',
  input:       '#12121a',
  inputBorder: 'rgba(255,255,255,0.15)',
};

// ── Constants ─────────────────────────────────────────────────────────────────

const ORDER_STATUSES = [
  { id: 'pending',    nl: 'In behandeling', en: 'Pending',    color: '#D4873A' },
  { id: 'confirmed',  nl: 'Bevestigd',      en: 'Confirmed',  color: '#5B9CF6' },
  { id: 'processing', nl: 'Verwerking',     en: 'Processing', color: '#9B59B6' },
  { id: 'shipped',    nl: 'Verzonden',      en: 'Shipped',    color: '#2A9E82' },
  { id: 'completed',  nl: 'Voltooid',       en: 'Completed',  color: '#4AC87A' },
  { id: 'cancelled',  nl: 'Geannuleerd',    en: 'Cancelled',  color: '#C05050' },
  { id: 'refunded',   nl: 'Terugbetaald',   en: 'Refunded',   color: '#667799' },
];

const STATUS_MAP = Object.fromEntries(ORDER_STATUSES.map(s => [s.id, s]));

const VAT_RATES = [0, 6, 9, 21];

const EMPTY_CUSTOMER = {
  customer_name: '', customer_email: '', customer_phone: '',
  shipping_address: '', notes: '',
};

const EMPTY_ITEM = { product_name: '', qty: 1, unit_price: '', vat_pct: 21 };

// ── Helpers ───────────────────────────────────────────────────────────────────

const statusCfg = (id) => STATUS_MAP[id] || { nl: id, en: id, color: '#667799' };

const formatDate = (str) => {
  if (!str) return '—';
  const d = new Date(str);
  return isNaN(d.getTime()) ? str : d.toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' });
};

const calcTotals = (items) => {
  let subtotal  = 0;
  let vat_total = 0;
  items.forEach(item => {
    const lineNet = (parseFloat(item.unit_price) || 0) * (parseInt(item.qty) || 0);
    const lineVat = lineNet * ((parseFloat(item.vat_pct) || 0) / 100);
    subtotal  += lineNet;
    vat_total += lineVat;
  });
  return { subtotal, vat_total, total: subtotal + vat_total };
};

const genOrderRef = () => {
  const now = new Date();
  return `ORD-${now.getFullYear()}-${String(Math.floor(Math.random() * 9000) + 1000)}`;
};

const parseItems = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch (_) {}
  }
  return [];
};

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <div style={{
      color: _T.accent, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
      textTransform: 'uppercase', marginBottom: 10, marginTop: 4,
    }}>
      {children}
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ color: C.silver, fontSize: 11, marginBottom: 5 }}>{label}</div>
      {children}
    </div>
  );
}

function StatusBadge({ status, nl: isNl }) {
  const cfg = statusCfg(status);
  return (
    <span style={{
      background: `${cfg.color}22`, border: `1px solid ${cfg.color}55`,
      borderRadius: 8, color: cfg.color, fontSize: 10, fontWeight: 700,
      padding: '2px 7px', whiteSpace: 'nowrap',
    }}>
      {isNl ? cfg.nl : cfg.en}
    </span>
  );
}

function OrderCard({ order, onClick, nl: isNl }) {
  const items  = parseItems(order.items);
  const totals = order.total !== undefined && order.total !== null
    ? { total: order.total }
    : calcTotals(items);

  return (
    <div
      onClick={onClick}
      style={{
        background: _T.bg3,
        border: `1px solid ${_T.cardBorderBot}`,
        borderRadius: 16,
        padding: '14px 16px',
        marginBottom: 12,
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div>
          <div style={{ color: _T.accent, fontSize: 12, fontWeight: 700, marginBottom: 3 }}>
            {order.order_ref || `#${order.id}`}
          </div>
          <div style={{ color: C.white, fontSize: 14, fontWeight: 700 }}>
            {order.customer_name || (isNl ? 'Onbekend' : 'Unknown')}
          </div>
          {order.customer_email && (
            <div style={{ color: C.silver, fontSize: 11, marginTop: 1 }}>{order.customer_email}</div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5 }}>
          <StatusBadge status={order.status || 'pending'} nl={isNl} />
          <span style={{ color: C.white, fontSize: 15, fontWeight: 800 }}>
            €{parseFloat(totals.total || 0).toFixed(2)}
          </span>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: C.silver, fontSize: 11 }}>
          {items.length} {isNl ? 'artikel(en)' : 'item(s)'}
        </span>
        <span style={{ color: C.silver, fontSize: 11 }}>
          {formatDate(order.created_at)}
        </span>
      </div>
    </div>
  );
}

// ── Slide-up Sheet ────────────────────────────────────────────────────────────

function Sheet({ open, onClose, children }) {
  if (!open) return null;
  return (
    <Portal>
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <style>{CSS}</style>
      <style>{`@keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>
      <div style={{
        background: F.bg,
        borderTop: `1.5px solid ${F.border}`,
        borderRadius: '20px 20px 0 0',
        maxHeight: '92vh',
        display: 'flex',
        flexDirection: 'column',
        animation: 'slideUp 0.28s cubic-bezier(0.32,0.72,0,1) both',
        boxShadow: '0 -8px 40px rgba(0,0,0,0.5)',
      }}>
        {/* Drag handle */}
        <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'center', padding: '12px 0 6px' }}
             onClick={onClose}>
          <div style={{ width: 44, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.2)', cursor: 'pointer' }} />
        </div>
        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 18px 36px' }}>
          {children}
        </div>
      </div>
    </div>
    </Portal>
  );
}

// ── Order Detail Sheet ────────────────────────────────────────────────────────

function OrderDetail({ order, onClose, onStatusChange, canEdit, nl: isNl }) {
  const items  = parseItems(order.items);
  const totals = calcTotals(items);

  const inputStyle = {
    background: F.input, border: `1px solid ${F.inputBorder}`,
    borderRadius: 10, padding: '9px 12px', color: C.white,
    fontSize: 13, width: '100%', boxSizing: 'border-box',
    outline: 'none', fontFamily: 'Inter,system-ui,sans-serif',
  };
  const panelStyle = {
    background: F.panel, border: `1px solid ${F.border}`,
    borderRadius: 14, padding: '12px 14px', marginBottom: 14,
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ color: _T.accent, fontSize: 12, fontWeight: 700 }}>{order.order_ref || `#${order.id}`}</div>
          <div style={{ color: C.white, fontSize: 16, fontWeight: 800, marginTop: 2 }}>
            {order.customer_name || (isNl ? 'Onbekend' : 'Unknown')}
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: C.silver, fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
      </div>

      {/* Status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <StatusBadge status={order.status || 'pending'} nl={isNl} />
        <span style={{ color: C.silver, fontSize: 11 }}>{formatDate(order.created_at)}</span>
      </div>

      {/* Status update */}
      {canEdit && (
        <div style={{ marginBottom: 18 }}>
          <div style={{ color: C.silver, fontSize: 11, marginBottom: 6 }}>{isNl ? 'Status wijzigen' : 'Update Status'}</div>
          <select
            style={inputStyle}
            value={order.status || 'pending'}
            onChange={e => onStatusChange(order.id, e.target.value)}
          >
            {ORDER_STATUSES.map(s => (
              <option key={s.id} value={s.id}>{isNl ? s.nl : s.en}</option>
            ))}
          </select>
        </div>
      )}

      {/* Customer info */}
      <SectionLabel>{isNl ? 'Klantgegevens' : 'Customer Info'}</SectionLabel>
      <div style={panelStyle}>
        {[
          [isNl ? 'Naam' : 'Name', order.customer_name],
          [isNl ? 'E-mail' : 'Email', order.customer_email],
          [isNl ? 'Telefoon' : 'Phone', order.customer_phone],
        ].map(([label, val]) => val ? (
          <div key={label} style={{ display: 'flex', gap: 10, marginBottom: 7 }}>
            <span style={{ color: C.silver, fontSize: 12, minWidth: 70 }}>{label}</span>
            <span style={{ color: C.white, fontSize: 13 }}>{val}</span>
          </div>
        ) : null)}
      </div>

      {/* Shipping address */}
      {order.shipping_address && (
        <>
          <SectionLabel>{isNl ? 'Bezorgadres' : 'Shipping Address'}</SectionLabel>
          <div style={panelStyle}>
            <div style={{ color: C.white, fontSize: 13, whiteSpace: 'pre-wrap' }}>{order.shipping_address}</div>
          </div>
        </>
      )}

      {/* Items */}
      <SectionLabel>{isNl ? 'Artikelen' : 'Items'}</SectionLabel>
      <div style={panelStyle}>
        {items.length === 0 && (
          <div style={{ color: C.silver, fontSize: 12 }}>{isNl ? 'Geen artikelen' : 'No items'}</div>
        )}
        {items.map((item, idx) => {
          const lineNet = (parseFloat(item.unit_price) || 0) * (parseInt(item.qty) || 0);
          const lineVat = lineNet * ((parseFloat(item.vat_pct) || 0) / 100);
          return (
            <div key={idx} style={{
              borderBottom: idx < items.length - 1 ? `1px solid ${F.border}` : 'none',
              paddingBottom: idx < items.length - 1 ? 10 : 0,
              marginBottom: idx < items.length - 1 ? 10 : 0,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{item.product_name || '—'}</div>
                  <div style={{ color: C.silver, fontSize: 11, marginTop: 2 }}>
                    {item.qty}× €{parseFloat(item.unit_price || 0).toFixed(2)} · BTW {item.vat_pct || 0}%
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: C.white, fontSize: 13, fontWeight: 700 }}>€{lineNet.toFixed(2)}</div>
                  <div style={{ color: C.silver, fontSize: 11 }}>+€{lineVat.toFixed(2)} {isNl ? 'BTW' : 'VAT'}</div>
                </div>
              </div>
            </div>
          );
        })}
        {items.length > 0 && (
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${F.border}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ color: C.silver, fontSize: 12 }}>{isNl ? 'Subtotaal' : 'Subtotal'}</span>
              <span style={{ color: C.white, fontSize: 12 }}>€{totals.subtotal.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ color: C.silver, fontSize: 12 }}>{isNl ? 'BTW' : 'VAT'}</span>
              <span style={{ color: C.white, fontSize: 12 }}>€{totals.vat_total.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
              <span style={{ color: C.white, fontSize: 14, fontWeight: 800 }}>{isNl ? 'Totaal' : 'Total'}</span>
              <span style={{ color: _T.accent, fontSize: 16, fontWeight: 800 }}>€{totals.total.toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Notes */}
      {order.notes && (
        <>
          <SectionLabel>{isNl ? 'Opmerkingen' : 'Notes'}</SectionLabel>
          <div style={panelStyle}>
            <div style={{ color: C.white, fontSize: 13, whiteSpace: 'pre-wrap' }}>{order.notes}</div>
          </div>
        </>
      )}
    </div>
  );
}

// ── New Order Form ────────────────────────────────────────────────────────────

function NewOrderForm({ onSave, onCancel, saving, products, customers, nl: isNl }) {
  const [customerForm, setCustomerForm] = useState(EMPTY_CUSTOMER);
  const [items,        setItems]        = useState([{ ...EMPTY_ITEM }]);
  const [itemSearches, setItemSearches] = useState(['']);
  const [activeAc,     setActiveAc]     = useState(-1);
  const [custSearch,   setCustSearch]   = useState('');
  const [custAcOpen,   setCustAcOpen]   = useState(false);

  const inputStyle = {
    background: F.input, border: `1px solid ${F.inputBorder}`,
    borderRadius: 10, padding: '9px 12px', color: C.white,
    fontSize: 13, width: '100%', boxSizing: 'border-box',
    outline: 'none', fontFamily: 'Inter,system-ui,sans-serif',
  };
  const panelStyle = {
    background: F.panel, border: `1px solid ${F.border}`,
    borderRadius: 16, padding: '14px 16px', marginBottom: 12,
  };

  const cInp = (field) => (e) => setCustomerForm(f => ({ ...f, [field]: e.target.value }));

  // Customer autocomplete from CRM
  const custResults = custSearch.trim().length > 0
    ? (customers || []).filter(c => {
        const q = custSearch.toLowerCase();
        return (c.first_name + ' ' + c.last_name).toLowerCase().includes(q)
          || (c.email || '').toLowerCase().includes(q)
          || (c.phone || '').toLowerCase().includes(q);
      }).slice(0, 6)
    : [];

  const pickCustomer = (c) => {
    setCustomerForm({
      customer_name:    `${c.first_name || ''} ${c.last_name || ''}`.trim(),
      customer_email:   c.email || '',
      customer_phone:   c.phone || '',
      shipping_address: c.address || '',
      notes: '',
    });
    setCustSearch('');
    setCustAcOpen(false);
  };

  const setItem = (idx, field, val) =>
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: val } : it));

  const addItem = () => {
    setItems(prev => [...prev, { ...EMPTY_ITEM }]);
    setItemSearches(prev => [...prev, '']);
  };

  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
    setItemSearches(prev => prev.filter((_, i) => i !== idx));
    if (activeAc === idx) setActiveAc(-1);
  };

  const handleProductSearch = (idx, val) => {
    setItem(idx, 'product_name', val);
    setItemSearches(prev => prev.map((s, i) => i === idx ? val : s));
    setActiveAc(val.trim().length > 0 ? idx : -1);
  };

  const handleProductSelect = (idx, p) => {
    setItem(idx, 'product_name', p.name);
    setItem(idx, 'unit_price', p.price ?? '');
    setItem(idx, 'vat_pct', p.vat_pct ?? 21);
    setItemSearches(prev => prev.map((s, i) => i === idx ? p.name : s));
    setActiveAc(-1);
  };

  const getAcResults = (idx) => {
    const q = (itemSearches[idx] || '').toLowerCase().trim();
    if (!q || activeAc !== idx) return [];
    return products
      .filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.sku  || '').toLowerCase().includes(q)
      )
      .slice(0, 6);
  };

  const totals = calcTotals(items);

  const handleSubmit = () => {
    if (!customerForm.customer_name.trim()) return;
    onSave({
      ...customerForm,
      order_ref: genOrderRef(),
      status: 'pending',
      items: JSON.stringify(items.filter(it => it.product_name.trim() !== '')),
      subtotal:  totals.subtotal,
      vat_total: totals.vat_total,
      total:     totals.total,
    });
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <span style={{ color: C.white, fontSize: 16, fontWeight: 700 }}>
          {isNl ? 'Nieuwe bestelling' : 'New Order'}
        </span>
        <button onClick={onCancel} style={{ background: 'transparent', border: 'none', color: C.silver, fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
      </div>

      {/* ── CUSTOMER ── */}
      <SectionLabel>{isNl ? 'Klantgegevens' : 'Customer Info'}</SectionLabel>

      {/* Customer search from CRM */}
      <div style={{ position: 'relative', marginBottom: 10 }}>
        <input
          style={inputStyle}
          value={custSearch}
          onChange={e => { setCustSearch(e.target.value); setCustAcOpen(true); }}
          onFocus={() => setCustAcOpen(true)}
          onBlur={() => setTimeout(() => setCustAcOpen(false), 180)}
          placeholder={isNl ? '🔍 Zoek bestaande klant…' : '🔍 Search existing customer…'}
          autoComplete="off"
        />
        {custAcOpen && custResults.length > 0 && (
          <div style={{
            position: 'absolute', zIndex: 300,
            background: F.panel, border: `1px solid ${F.inputBorder}`,
            borderRadius: 12, width: '100%', top: '100%', marginTop: 4,
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          }}>
            {custResults.map(c => (
              <div
                key={c.id}
                onMouseDown={() => pickCustomer(c)}
                style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${F.border}` }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>
                  {c.first_name} {c.last_name}
                </div>
                {(c.email || c.phone) && (
                  <div style={{ color: C.silver, fontSize: 11, marginTop: 2 }}>
                    {[c.email, c.phone].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {custAcOpen && custSearch.trim().length > 0 && custResults.length === 0 && (
          <div style={{
            position: 'absolute', zIndex: 300,
            background: F.panel, border: `1px solid ${F.inputBorder}`,
            borderRadius: 12, width: '100%', top: '100%', marginTop: 4,
            padding: '10px 14px',
          }}>
            <div style={{ color: C.silver, fontSize: 12 }}>
              {isNl ? 'Geen klanten gevonden — vul onderstaande velden in' : 'No customers found — fill in the fields below'}
            </div>
          </div>
        )}
      </div>

      <div style={panelStyle}>
        <FieldRow label={isNl ? 'Naam *' : 'Name *'}>
          <input style={inputStyle} value={customerForm.customer_name} onChange={cInp('customer_name')} placeholder={isNl ? 'Volledige naam' : 'Full name'} />
        </FieldRow>
        <FieldRow label={isNl ? 'E-mail' : 'Email'}>
          <input style={inputStyle} type="email" value={customerForm.customer_email} onChange={cInp('customer_email')} placeholder="naam@voorbeeld.nl" />
        </FieldRow>
        <FieldRow label={isNl ? 'Telefoon' : 'Phone'}>
          <input style={inputStyle} type="tel" value={customerForm.customer_phone} onChange={cInp('customer_phone')} placeholder="+31 6 12345678" />
        </FieldRow>
      </div>

      {/* ── SHIPPING ── */}
      <SectionLabel>{isNl ? 'Bezorgadres' : 'Shipping Address'}</SectionLabel>
      <div style={panelStyle}>
        <textarea
          style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }}
          value={customerForm.shipping_address}
          onChange={cInp('shipping_address')}
          placeholder={isNl ? 'Straat, huisnr, postcode, stad' : 'Street, number, postcode, city'}
        />
      </div>

      {/* ── LINE ITEMS ── */}
      <SectionLabel>{isNl ? 'Artikelen' : 'Line Items'}</SectionLabel>
      <div style={panelStyle}>
        {items.map((item, idx) => {
          const acResults = getAcResults(idx);
          return (
            <div key={idx} style={{
              borderBottom: idx < items.length - 1 ? `1px solid ${F.border}` : 'none',
              paddingBottom: idx < items.length - 1 ? 12 : 0,
              marginBottom: idx < items.length - 1 ? 12 : 0,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ color: C.silver, fontSize: 11 }}>{isNl ? `Artikel ${idx + 1}` : `Item ${idx + 1}`}</span>
                {items.length > 1 && (
                  <button
                    onClick={() => removeItem(idx)}
                    style={{ background: 'transparent', border: 'none', color: C.red, fontSize: 13, cursor: 'pointer', padding: '0 2px' }}
                  >
                    {isNl ? 'Verwijder' : 'Remove'}
                  </button>
                )}
              </div>

              {/* Product name with autocomplete */}
              <FieldRow label={isNl ? 'Productnaam' : 'Product Name'}>
                <div style={{ position: 'relative' }}>
                  <input
                    style={inputStyle}
                    value={item.product_name}
                    onChange={e => handleProductSearch(idx, e.target.value)}
                    onFocus={() => { if (item.product_name.trim()) setActiveAc(idx); }}
                    onBlur={() => setTimeout(() => setActiveAc(-1), 150)}
                    placeholder={isNl ? 'Typ om te zoeken...' : 'Type to search...'}
                    autoComplete="off"
                  />
                  {acResults.length > 0 && (
                    <div style={{
                      position: 'absolute', zIndex: 100,
                      background: F.panel,
                      border: `1px solid ${F.inputBorder}`,
                      borderRadius: 10, width: '100%',
                      maxHeight: 200, overflowY: 'auto',
                      top: '100%', left: 0, marginTop: 2,
                    }}>
                      {acResults.map(p => (
                        <div
                          key={p.id}
                          onMouseDown={() => handleProductSelect(idx, p)}
                          style={{
                            padding: '8px 12px', cursor: 'pointer',
                            borderBottom: `1px solid ${F.border}`,
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{p.name}</div>
                          {p.price !== null && p.price !== undefined && p.price !== '' && (
                            <div style={{ color: C.silver, fontSize: 11, marginTop: 2 }}>
                              €{parseFloat(p.price).toFixed(2)} {isNl ? 'excl. BTW' : 'ex VAT'}
                              {p.sku ? ` · ${p.sku}` : ''}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </FieldRow>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <FieldRow label={isNl ? 'Aantal' : 'Qty'}>
                  <input
                    style={inputStyle}
                    type="number" min="1"
                    value={item.qty}
                    onChange={e => setItem(idx, 'qty', e.target.value)}
                  />
                </FieldRow>
                <FieldRow label={isNl ? 'Stukprijs (€)' : 'Unit Price (€)'}>
                  <input
                    style={inputStyle}
                    type="number" min="0" step="0.01"
                    value={item.unit_price}
                    onChange={e => setItem(idx, 'unit_price', e.target.value)}
                    placeholder="0.00"
                  />
                </FieldRow>
                <FieldRow label={isNl ? 'BTW %' : 'VAT %'}>
                  <select
                    style={inputStyle}
                    value={item.vat_pct}
                    onChange={e => setItem(idx, 'vat_pct', e.target.value)}
                  >
                    {VAT_RATES.map(v => <option key={v} value={v}>{v}%</option>)}
                  </select>
                </FieldRow>
              </div>

              {/* Line total preview */}
              {item.unit_price !== '' && item.product_name.trim() !== '' && (
                <div style={{ color: C.silver, fontSize: 11, textAlign: 'right', marginTop: 2 }}>
                  = €{((parseFloat(item.unit_price) || 0) * (parseInt(item.qty) || 0) * (1 + (parseFloat(item.vat_pct) || 0) / 100)).toFixed(2)} {isNl ? 'incl. BTW' : 'incl. VAT'}
                </div>
              )}
            </div>
          );
        })}

        <button
          onClick={addItem}
          style={{
            marginTop: 12, background: 'transparent',
            border: `1px dashed ${_T.accent}55`, borderRadius: 10,
            padding: '8px 14px', color: _T.accent, fontSize: 12,
            cursor: 'pointer', width: '100%',
          }}
        >
          + {isNl ? 'Artikel toevoegen' : 'Add Item'}
        </button>

        {/* Totals summary */}
        {items.some(it => it.product_name.trim() !== '' && it.unit_price !== '') && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${F.border}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ color: C.silver, fontSize: 12 }}>{isNl ? 'Subtotaal' : 'Subtotal'}</span>
              <span style={{ color: C.white, fontSize: 12 }}>€{totals.subtotal.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ color: C.silver, fontSize: 12 }}>{isNl ? 'BTW' : 'VAT'}</span>
              <span style={{ color: C.white, fontSize: 12 }}>€{totals.vat_total.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              <span style={{ color: C.white, fontSize: 14, fontWeight: 800 }}>{isNl ? 'Totaal' : 'Total'}</span>
              <span style={{ color: _T.accent, fontSize: 16, fontWeight: 800 }}>€{totals.total.toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>

      {/* ── NOTES ── */}
      <SectionLabel>{isNl ? 'Opmerkingen' : 'Notes'}</SectionLabel>
      <div style={{ ...panelStyle, marginBottom: 20 }}>
        <textarea
          style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
          value={customerForm.notes}
          onChange={cInp('notes')}
          placeholder={isNl ? 'Interne opmerkingen...' : 'Internal notes...'}
        />
      </div>

      {/* ── Submit ── */}
      <button
        onClick={handleSubmit}
        disabled={saving || !customerForm.customer_name.trim()}
        style={{
          width: '100%',
          background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`,
          border: 'none', borderRadius: 12, padding: '12px 20px',
          color: '#0a1220', fontWeight: 700, fontSize: 14, cursor: 'pointer',
          opacity: saving || !customerForm.customer_name.trim() ? 0.5 : 1,
        }}
      >
        {saving ? '...' : (isNl ? 'Bestelling aanmaken' : 'Create Order')}
      </button>
    </div>
  );
}

// ── Main Screen ───────────────────────────────────────────────────────────────

export default function BusinessOrdersScreen({ lang, businessId, role }) {
  const nl      = lang === 'nl';
  const canEdit = role === 'owner' || role === 'admin';

  const [orders,      setOrders]      = useState([]);
  const [products,    setProducts]    = useState([]);
  const [customers,   setCustomers]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [statusTab,   setStatusTab]   = useState('all');
  const [detailOrder, setDetailOrder] = useState(null);
  const [showNew,     setShowNew]     = useState(false);
  const [saving,      setSaving]      = useState(false);

  // ── Load products + customers (for autocomplete) ──
  useEffect(() => {
    fetch(`/api/businesses/${businessId}/products`)
      .then(r => r.json())
      .then(d => setProducts(Array.isArray(d) ? d : []))
      .catch(() => {});
    fetch(`/api/businesses/${businessId}/customers`)
      .then(r => r.json())
      .then(d => setCustomers(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [businessId]);

  // ── Load orders ──
  const loadOrders = useCallback(() => {
    setLoading(true);
    fetch(`/api/businesses/${businessId}/orders`)
      .then(r => r.json())
      .then(data => { setOrders(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [businessId]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  // ── Status update ──
  const handleStatusChange = async (orderId, newStatus) => {
    await fetch(`/api/businesses/${businessId}/orders/${orderId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    }).catch(() => {});
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
    setDetailOrder(prev => prev && prev.id === orderId ? { ...prev, status: newStatus } : prev);
  };

  // ── Create order ──
  const handleCreateOrder = async (payload) => {
    setSaving(true);
    try {
      await fetch(`/api/businesses/${businessId}/orders`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    } catch (_) {}
    setSaving(false);
    setShowNew(false);
    loadOrders();
  };

  // ── Filter ──
  const filtered = orders.filter(o => {
    const q = search.toLowerCase();
    const matchSearch = !q || [o.customer_name, o.customer_email, o.order_ref]
      .some(v => (v || '').toLowerCase().includes(q));
    const matchStatus = statusTab === 'all' || o.status === statusTab;
    return matchSearch && matchStatus;
  });

  // ── Tab counts ──
  const counts = { all: orders.length };
  ORDER_STATUSES.forEach(s => { counts[s.id] = orders.filter(o => o.status === s.id).length; });

  const TABS = [
    { id: 'all',        nl: 'Alle',          en: 'All' },
    { id: 'pending',    nl: 'In behandeling', en: 'Pending' },
    { id: 'processing', nl: 'Verwerking',    en: 'Processing' },
    { id: 'completed',  nl: 'Voltooid',      en: 'Completed' },
    { id: 'cancelled',  nl: 'Geannuleerd',   en: 'Cancelled' },
  ];

  // ── Revenue summary ──
  const completedRevenue = orders
    .filter(o => o.status === 'completed')
    .reduce((sum, o) => sum + (parseFloat(o.total) || 0), 0);

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ paddingBottom: 32 }}>
      <style>{CSS}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ color: C.white, fontSize: 18, fontWeight: 800 }}>
            {nl ? 'Bestellingen' : 'Orders'}
          </div>
          <div style={{ color: C.silver, fontSize: 12, marginTop: 2 }}>
            {orders.length} {nl ? 'bestellingen' : 'orders'}
          </div>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowNew(true)}
            style={{
              background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`,
              border: 'none', borderRadius: 12, padding: '10px 20px',
              color: '#0a1220', fontWeight: 700, fontSize: 13, cursor: 'pointer',
            }}
          >
            + {nl ? 'Bestelling' : 'Order'}
          </button>
        )}
      </div>

      {/* Revenue summary card */}
      {orders.length > 0 && (
        <div style={{
          background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`,
          borderRadius: 16, padding: '14px 16px', marginBottom: 16,
          display: 'flex', gap: 16,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ color: C.silver, fontSize: 11, marginBottom: 3 }}>
              {nl ? 'Omzet (voltooid)' : 'Revenue (completed)'}
            </div>
            <div style={{ color: _T.accent, fontSize: 20, fontWeight: 800 }}>
              €{completedRevenue.toFixed(2)}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: C.silver, fontSize: 11, marginBottom: 3 }}>
              {nl ? 'Open bestellingen' : 'Open orders'}
            </div>
            <div style={{ color: C.white, fontSize: 20, fontWeight: 800 }}>
              {orders.filter(o => ['pending', 'confirmed', 'processing'].includes(o.status)).length}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: C.silver, fontSize: 11, marginBottom: 3 }}>
              {nl ? 'Verzonden' : 'Shipped'}
            </div>
            <div style={{ color: '#2A9E82', fontSize: 20, fontWeight: 800 }}>
              {orders.filter(o => o.status === 'shipped').length}
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div style={{ marginBottom: 14 }}>
        <input
          style={s_TI({ fontSize: 13, padding: '9px 12px' })}
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={nl ? '🔍 Zoek klant, e-mail, bestelnummer...' : '🔍 Search customer, email, order ref...'}
        />
      </div>

      {/* Status filter tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 18, overflowX: 'auto', paddingBottom: 2 }}>
        {TABS.map(t => {
          const active = statusTab === t.id;
          const cfg    = ORDER_STATUSES.find(s => s.id === t.id);
          const col    = cfg ? cfg.color : _T.accent;
          return (
            <button
              key={t.id}
              onClick={() => setStatusTab(t.id)}
              style={{
                background: active ? `${col}22` : _T.bg3,
                border: `1px solid ${active ? col : _T.cardBorderBot}`,
                borderRadius: 20, padding: '6px 14px', cursor: 'pointer',
                color: active ? col : C.silver, fontSize: 12, fontWeight: active ? 700 : 400,
                whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              {nl ? t.nl : t.en}
              <span style={{ marginLeft: 5, opacity: 0.65 }}>({counts[t.id] || 0})</span>
            </button>
          );
        })}
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', color: C.silver, fontSize: 13, padding: 40 }}>...</div>
      )}

      {/* Empty */}
      {!loading && filtered.length === 0 && (
        <div style={{
          textAlign: 'center', color: C.silver, fontSize: 13, padding: 40,
          background: _T.bg3, borderRadius: 16, border: `1px solid ${_T.cardBorderBot}`,
        }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>📋</div>
          {nl ? 'Geen bestellingen gevonden' : 'No orders found'}
        </div>
      )}

      {/* Order list */}
      {!loading && filtered.map(o => (
        <OrderCard
          key={o.id}
          order={o}
          onClick={() => setDetailOrder(o)}
          nl={nl}
        />
      ))}

      {/* Order detail sheet */}
      <Sheet open={!!detailOrder && !showNew} onClose={() => setDetailOrder(null)}>
        {detailOrder && (
          <OrderDetail
            order={detailOrder}
            onClose={() => setDetailOrder(null)}
            onStatusChange={handleStatusChange}
            canEdit={canEdit}
            nl={nl}
          />
        )}
      </Sheet>

      {/* New order sheet */}
      <Sheet open={showNew} onClose={() => setShowNew(false)}>
        <NewOrderForm
          onSave={handleCreateOrder}
          onCancel={() => setShowNew(false)}
          saving={saving}
          products={products}
          customers={customers}
          nl={nl}
        />
      </Sheet>
    </div>
  );
}
